import React, { useEffect, useMemo } from 'react';
import {
  ChatWidgetLog,
  ChatWidgetLogLevel,
  MessageRequest,
} from '../../domain/logs';
import { ChatMessageData } from '@wix/chat-widget-ui-lib';
import { convertPlatformLogs } from './convertPlatformLogs';
import { v4 as uuid } from 'uuid';
import { useIsConversationActive } from './useIsConversationActive';
import { LiveChat } from '../../../types';

export const useLiveChat = (agentName?: string) => {
  const [chat, setChat] = React.useState<LiveChat | null>(null);
  const { isConversationActive, conversationId, setConversationId } =
    useIsConversationActive();
  const [requests, setRequests] = React.useState<MessageRequest[]>([]);
  const [messages, setMessages] = React.useState<ChatMessageData[]>([]);
  const [statusMessage, setStatusMessage] = React.useState<string>();

  useEffect(() => {}, [chat]);

  const createLoggerWithMeasure = useMemo(() => {
    return (userMessageContent: string) => {
      const requestStart = Date.now();
      return (logs: ChatWidgetLog[], requestId: string) => {
        setRequests((prev) => [
          ...prev,
          {
            requestId,
            userMessageContent,
            responseTimeMs: Date.now() - requestStart,
            logs,
          },
        ]);
      };
    };
  }, []);

  const lastMessage = messages.slice(-1)[0];

  useEffect(() => {
    const waitingForAnswer = lastMessage && lastMessage.role === 'user';
    if (!chat || !isConversationActive() || !waitingForAnswer) {
      return;
    }
    const statusInterval = setInterval(async () => {
      if (!waitingForAnswer) {
        return;
      }
      const message = await chat.getConversationStatus(conversationId!);
      if (!isConversationActive()) {
        return;
      }
      setStatusMessage(message);
    }, 500);
    return () => clearInterval(statusInterval);
  }, [messages, chat, isConversationActive, lastMessage, conversationId]);

  const sendMessage = async (userMessageContent: string) => {
    if (!chat) {
      throw new Error('Chat is not initialized');
    }
    const logRequest = createLoggerWithMeasure(userMessageContent);
    try {
      const tempUserMessageId = uuid();
      setMessages((prev) => [
        ...prev,
        {
          id: tempUserMessageId,
          content: userMessageContent,
          role: 'user',
        },
      ]);
      const res = await chat.sendUserMessage(
        userMessageContent as string,
        agentName || null,
      );

      if (!isConversationActive()) {
        return;
      }

      if (!res?.ok) {
        throw new Error(res?.cause);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.content,
        },
      ]);
      logRequest(convertPlatformLogs(res.logs), res.requestId);
      return;
    } catch (error: any) {
      console.error(error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `<<{"widget":"FALLBACK_WIDGET","data":{"errorType":"http","errorCode":"${error.code}"}}>>`,
        },
      ]);
      logRequest(
        [
          {
            id: uuid(),
            level: ChatWidgetLogLevel.Error,
            timestamp: new Date(),
            chatLog: {
              message: error.message,
              jsonData: JSON.stringify(error.response?.data) ?? null,
            },
          },
        ],
        'unknown',
      );
    }
  };

  return {
    setChat: (cht: LiveChat) => {
      setChat(cht);
      setRequests([]);
      setStatusMessage(undefined);
      setConversationId(cht.id);
      setMessages([]);
    },
    sendMessage,
    conversationId,
    requests,
    messages,
    statusMessage,
    isConversationActive,
    createLoggerWithMeasure,
  };
};
